import ControlViewModel from '@/ViewModels/ControlViewModel';
import ButtonViewModel from '@/ViewModels/Controls/ButtonViewModel';
import WindowViewModel from '@/ViewModels/Controls/WindowViewModel';
import ImageViewModel from '@/ViewModels/Controls/ImageViewModel';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import ComponentManager from '@/Management/ComponentManager';
import ResourceManager from '@/Management/ResourceManager';
import LocatorHelper from '@/Utility/LocatorHelper';
import { IMessageBoxListItem, IMessageBoxListItemParams } from '#/DefaultViewExtensionInterfaces';
import GlobalConfiguration from '@/Management/GlobalConfiguration';
import Utilities from '@/Utility/Utilities';
import builder from '@/Builder/MessageBoxViewModelBuilder';

export default class MessageBoxViewModel extends ControlViewModel {

    private static MSG_WINDOW_SIZE = Utilities.isMobileView() ? '80vw' : 650;

    /**
     * Das Fenster
     */
    public window: WindowViewModel;

    /**
     * Das Icon
     */
    public image: ImageViewModel;

    /**
     * Der angezeigte Text
     */
    public label: LabelViewModel;

    /**
     * Die übermittelten Daten
     */
    public data: LabelViewModel;

    /**
     * Der angezeigte Stack
     */
    public stack: LabelViewModel;

    /**
     * Die angezeigte Liste von Texten
     */
    public textList: Array<string>;

    /**
     * Die angezeigte Texte mit entsprechenden Listen
     */
    public labelArray: Array<IMessageBoxListItem>; 

    /**
     * Die angezeigten Buttons
     */
    public buttons: Array<ButtonViewModel>;

    /**
     * Der Style der MessageBox
     */
    public style: string;

    constructor(params: any) {
        super(params);
        this.window = this.paramOrDefault(params.window, null);
        this.image = this.paramOrDefault(params.image, null);
        this.label = this.paramOrDefault(params.label, null);
        this.data = this.paramOrDefault(params.data, null);
        this.stack = this.paramOrDefault(params.stack, null);
        this.textList = this.paramOrDefault(params.textList, null);
        this.buttons = this.paramOrDefault(params.buttons, null);
        this.style = this.paramOrDefault(params.style, '');
        this.labelArray = this.paramOrDefault(params.labelArray, null);
    }

    public static createNoticeMessageBox(caption: ObservableOrDefault<string>, ergName?: ObservableOrDefault<string>, callback?: Function, additionalWindowParams?: any, listItemParams?: Array<IMessageBoxListItemParams>): MessageBoxViewModel {
        const defaultParams = {};

        return MessageBoxViewModel.createCustomNoticeMessageBox(caption, ergName, defaultParams, callback, additionalWindowParams, listItemParams);
    }

    public static createSaveWarningMessageBox(yesCallback: () => void, noCallback: () => void): MessageBoxViewModel {
        const loc = 'save-before-leaving-dv-dialog';

        return MessageBoxViewModel.createYesNoMessageBox(null, 'DETAILVIEW_SAVE_WARNING', yesCallback, noCallback, loc);
    }

    public static createCustomNoticeMessageBox(caption: ObservableOrDefault<string>, ergName: ObservableOrDefault<string>, customParams: customParamsType, callback?: Function, additionalWindowParams?: any, listItemParams?: Array<IMessageBoxListItemParams>): MessageBoxViewModel {
        const windowViewModel = ComponentManager.getViewModel<typeof WindowViewModel>('WindowViewModel');
        const labelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');
        const imageViewModel = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
        const buttonViewModel = ComponentManager.getViewModel<typeof ButtonViewModel>('ButtonViewModel');

        const loc = 'notice-message';

        const defaultParams: customParamsType = {
            window: {
                headline: { ergName: 'NOTICE_TITLE' },
                width: MessageBoxViewModel.MSG_WINDOW_SIZE,
                allowClose: true
            },
            image: {
                source: GlobalConfiguration.inlineImages.NOTICE_MESSAGE_ICON
            },
            button: {
                image: { source: GlobalConfiguration.inlineImages.WINDOW_OK },
                label: { ergName: 'OKAY' }
            }
        };

        const deep = true;
        const combinedParams = $.extend(deep, defaultParams, customParams);

        const buttons = customParams.buttons || [
            new buttonViewModel({
                parentLocator: loc,
                isPrimaryButton: true,
                locator: 'button.ok',
                image: new imageViewModel({
                    parentLocator: loc,
                    locator: 'button.ok.image',
                    source: combinedParams.button.image.source,
                    alternativeText: new labelViewModel({
                        ergName: 'OKAY'
                    })
                }),
                label: new labelViewModel({
                    parentLocator: loc,
                    locator: 'button.ok.label',
                    ergName: combinedParams.button.label.ergName
                }),
                onClick: () => {
                    messageBoxViewModel.window.isOpen(false);

                    if (callback != null) {
                        callback();
                    }
                }
            })
        ];

        const messageBoxViewModel = new MessageBoxViewModel({
            window: new windowViewModel($.extend({
                parentLocator: loc,
                locator: 'window',
                width: combinedParams.window.width,
                isModal: true,
                isOpen: true,
                allowClose: combinedParams.window.allowClose,
                headline: new labelViewModel({
                    ergName: combinedParams.window.headline.ergName
                })
            }, additionalWindowParams)),
            image: new imageViewModel({
                parentLocator: loc,
                locator: 'content.image',
                source: combinedParams.image.source,
                alternativeText: new labelViewModel({
                    ergName: ergName,
                    caption: caption
                })
            }),
            label: new labelViewModel({
                parentLocator: loc,
                locator: 'content.label',
                caption: caption,
                ergName: ergName
            }),
            labelArray: this.createArrayOfLabels(loc, listItemParams),
            buttons: buttons
        });

        return messageBoxViewModel;
    }

    private static createArrayOfLabels(loc, listItemParamsArray: Array<IMessageBoxListItemParams>) {
        let labelArray = [];
        if (listItemParamsArray != null && listItemParamsArray != undefined) {
            for (var i = 0; i < listItemParamsArray.length; i++) {
                labelArray.push(
                    {
                        label: new LabelViewModel({
                            parentLocator: loc,
                            locator: LocatorHelper.buildLocator('content', 'label', i),
                            caption: listItemParamsArray[i].text,
                            ergName: null
                        }),
                        list: listItemParamsArray[i].list
                    }
                )
            }
            return labelArray
        }
        return null;
    }

    public static createDetailViewDropMessageBox(callback: Function): MessageBoxViewModel {
        const windowViewModel = ComponentManager.getViewModel<typeof WindowViewModel>('WindowViewModel');
        const labelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');
        const imageViewModel = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
        const buttonViewModel = ComponentManager.getViewModel<typeof ButtonViewModel>('ButtonViewModel');

        const loc = 'cancel-on-leaving-dv-dialog-message';

        const messageBoxViewModel = new MessageBoxViewModel({
            window: new windowViewModel({
                parentLocator: loc,
                locator: 'window',
                width: MessageBoxViewModel.MSG_WINDOW_SIZE,
                isModal: true,
                isOpen: true,
                allowClose: true,
                headline: new labelViewModel({
                    ergName: 'MENU_ITEM_INFO'
                })
            }),
            image: new imageViewModel({
                parentLocator: loc,
                locator: 'content.image',
                source: GlobalConfiguration.inlineImages.NOTICE_MESSAGE_ICON,
                alternativeText: new labelViewModel({
                    ergName: 'NOTICE_TITLE'
                })
            }),
            label: new labelViewModel({
                parentLocator: loc,
                locator: 'content.label',
                ergName: 'DETAILVIEW_DROP_WARNING'
            }),
            buttons: [
                new buttonViewModel({
                    parentLocator: loc,
                    locator: 'button.ok',
                    isPrimaryButton: true,
                    image: new imageViewModel({
                        parentLocator: loc,
                        locator: 'button.ok.image',
                        source: GlobalConfiguration.inlineImages.WINDOW_OK,
                        alternativeText: new labelViewModel({
                            ergName: 'OKAY'
                        })
                    }),
                    label: new labelViewModel({
                        parentLocator: loc,
                        locator: 'button.ok.label',
                        ergName: 'YES'
                    }),
                    onClick: () => {
                        messageBoxViewModel.window.isOpen(false);
                        callback();
                    }
                }),
                new buttonViewModel({
                    parentLocator: loc,
                    locator: 'button.cancel',
                    image: new imageViewModel({
                        parentLocator: loc,
                        locator: 'button.cancel.image',
                        source: GlobalConfiguration.inlineImages.WINDOW_CANCEL,
                        alternativeText: new labelViewModel({
                            ergName: 'CANCEL'
                        })
                    }),
                    label: new labelViewModel({
                        parentLocator: loc,
                        locator: 'button.cancel.label',
                        ergName: 'NO'
                    }),
                    onClick: () => {
                        messageBoxViewModel.window.isOpen(false);
                    }
                })
            ]
        });

        return messageBoxViewModel;
    }

    public static createDeletionMessageBox(tryDeletion: any, yesCallback: Function, noCallback?: Function): MessageBoxViewModel {
        const windowViewModel = ComponentManager.getViewModel<typeof WindowViewModel>('WindowViewModel');
        const labelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');
        const imageViewModel = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
        const buttonViewModel = ComponentManager.getViewModel<typeof ButtonViewModel>('ButtonViewModel');

        const loc = 'deletion-warning-message';

        const headlineErgName = ko.computed(() => {
            if (!tryDeletion || tryDeletion.objectsToDelete.length === 1 && tryDeletion.objectsToDelete[0].objects.length === 1) {
                return 'MENU_ITEM_INFO';
            } else {
                return 'WARNING_TITLE';
            }
        });

        const headlineImageSource = ko.computed(() => {
            if (!tryDeletion || tryDeletion.objectsToDelete.length === 1) {
                return GlobalConfiguration.inlineImages.NOTICE_MESSAGE_ICON;
            } else {
                return 'message_warning.png';
            }
        });

        const labelErgName = ko.computed(() => {
            if (!tryDeletion || tryDeletion.objectsToDelete.length === 1) {
                return 'LISTVIEW_DELETE_WARNING';
            } else {
                return 'LISTVIEW_DELETE_WARNING_WITH_FOLLOWING';
            }
        });

        const labelCaption = ko.computed(() => {
            const result = [];
            if (tryDeletion) {
                for (let i = 0; i < tryDeletion.objectsToDelete.length; i++) {
                    const item = tryDeletion.objectsToDelete[i];
                    const label = ResourceManager.getInstance().loadResource(item.ergNamePluralized, true);
                    const count = item.objects.length;

                    result.push(count + 'x ' + label);
                }
            }
            return result.join(', ');
        });

        const messageBoxViewModel = new MessageBoxViewModel({
            window: new windowViewModel({
                parentLocator: loc,
                locator: 'window',
                width: MessageBoxViewModel.MSG_WINDOW_SIZE,
                isModal: true,
                isOpen: true,
                headline: new labelViewModel({
                    ergName: headlineErgName
                })
            }),
            image: new imageViewModel({
                parentLocator: loc,
                locator: 'content.image',
                source: headlineImageSource,
                alternativeText: new labelViewModel({
                    ergName: headlineErgName
                })
            }),
            label: new labelViewModel({
                parentLocator: loc,
                locator: 'content.label',
                ergName: labelErgName,
                replacements: {
                    items: new labelViewModel({
                        caption: labelCaption
                    })
                }
            }),
            buttons: [
                new buttonViewModel({
                    parentLocator: loc,
                    locator: 'button.ok',
                    isPrimaryButton: true,
                    image: new imageViewModel({
                        parentLocator: loc,
                        locator: 'button.ok.image',
                        source: GlobalConfiguration.inlineImages.WINDOW_OK,
                        alternativeText: new labelViewModel({
                            ergName: 'OKAY'
                        })
                    }),
                    label: new labelViewModel({
                        parentLocator: loc,
                        locator: 'button.ok.label',
                        ergName: 'YES'
                    }),
                    onClick: () => {
                        messageBoxViewModel.window.isOpen(false);

                        if (yesCallback != null) {
                            yesCallback();
                        }
                    }
                }),
                new buttonViewModel({
                    parentLocator: loc,
                    locator: 'button.cancel',
                    image: new imageViewModel({
                        parentLocator: loc,
                        locator: 'button.cancel.image',
                        source: GlobalConfiguration.inlineImages.WINDOW_CANCEL,
                        alternativeText: new labelViewModel({
                            ergName: 'CANCEL'
                        })
                    }),
                    label: new labelViewModel({
                        parentLocator: loc,
                        locator: 'button.cancel.label',
                        ergName: 'NO'
                    }),
                    onClick: () => {
                        messageBoxViewModel.window.isOpen(false);

                        if (noCallback != null) {
                            noCallback();
                        }
                    }
                })
            ]
        });

        messageBoxViewModel.addComputed(headlineErgName);
        messageBoxViewModel.addComputed(headlineImageSource);
        messageBoxViewModel.addComputed(labelErgName);
        messageBoxViewModel.addComputed(labelCaption);

        return messageBoxViewModel;
    }

    public static createLockedByUserMessageBox(lockedByUser: any, lockedObjectIdent: any, callback?: Function): MessageBoxViewModel {
        const windowViewModel = ComponentManager.getViewModel<typeof WindowViewModel>('WindowViewModel');
        const labelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');
        const imageViewModel = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
        const buttonViewModel = ComponentManager.getViewModel<typeof ButtonViewModel>('ButtonViewModel');

        const loc = 'object-locked-by-user-message';

        const messageBoxViewModel = new MessageBoxViewModel({
            window: new windowViewModel({
                parentLocator: loc,
                locator: 'window',
                width: MessageBoxViewModel.MSG_WINDOW_SIZE,
                isModal: true,
                isOpen: true,
                allowClose: true,
                isMessageBox: true,
                headline: new labelViewModel({
                    ergName: 'MENU_ITEM_INFO'
                })
            }),
            image: new imageViewModel({
                parentLocator: loc,
                locator: 'content.image',
                source: GlobalConfiguration.inlineImages.NOTICE_MESSAGE_ICON,
                alternativeText: new labelViewModel({
                    ergName: 'NOTICE_TITLE'
                })
            }),
            label: (() => {
                if (lockedByUser === '$$Task$$') {
                    return new labelViewModel({
                        parentLocator: loc,
                        locator: 'content.label',
                        ergName: 'LOCKED_BY_TASK_ERROR',
                    });
                }

                if (lockedObjectIdent) {
                    if (lockedByUser !== 'Unknown') {
                        return new labelViewModel({
                            parentLocator: loc,
                            locator: 'content.label',
                            ergName: 'LOCKED_BY_OTHER_OID_ERROR',
                            replacements: {
                                user: lockedByUser,
                                objectIdent: lockedObjectIdent
                            }
                        });
                    }

                    return new labelViewModel({
                        parentLocator: loc,
                        locator: 'content.label',
                        ergName: 'LOCKED_BY_UNKNOWN_ERROR',
                        replacements: {
                            objectIdent: lockedObjectIdent
                        }
                    });
                }

                return new labelViewModel({
                    parentLocator: loc,
                    locator: 'content.label',
                    ergName: 'LOCKED_BY_OTHER_ERROR',
                    replacements: {
                        user: lockedByUser
                    }
                });
            })(),
            buttons: [
                new buttonViewModel({
                    parentLocator: loc,
                    locator: 'button.ok',
                    isPrimaryButton: true,
                    image: new imageViewModel({
                        parentLocator: loc,
                        locator: 'button.ok.image',
                        source: GlobalConfiguration.inlineImages.WINDOW_OK,
                        alternativeText: new labelViewModel({
                            ergName: 'OKAY'
                        })
                    }),
                    label: new labelViewModel({
                        parentLocator: loc,
                        locator: 'button.ok.label',
                        ergName: 'OKAY'
                    }),
                    onClick: () => {
                        messageBoxViewModel.window.isOpen(false);

                        if (callback != null) {
                            callback();
                        }
                    }
                })
            ]
        });

        return messageBoxViewModel;
    }

    public static createWarningMessageBox(text: ObservableOrDefault<string>, callback?: Function, additionalWindowParams?: any, showOkCancelButtons?: boolean): MessageBoxViewModel {
        return new builder()
            .initForWarningMessageBox(text, callback, showOkCancelButtons)
            .withWindow()
            .updateProperties(additionalWindowParams)
            .done()
            .build();
    }

    public static createErrorMessageBox(caption: ObservableOrDefault<string>, ergName: ObservableOrDefault<string>, stack?: string, data?: any, callback?: Function, additionalWindowParams?: any): MessageBoxViewModel {
        const windowViewModel = ComponentManager.getViewModel<typeof WindowViewModel>('WindowViewModel');
        const labelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');
        const imageViewModel = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
        const buttonViewModel = ComponentManager.getViewModel<typeof ButtonViewModel>('ButtonViewModel');

        if (caption == null && ergName == null) {
            ergName = 'GENERIC_ERROR';
        }

        const loc = 'error-message';

        const messageBoxViewModel = new MessageBoxViewModel({
            window: new windowViewModel($.extend({
                parentLocator: loc,
                locator: 'window',
                width: MessageBoxViewModel.MSG_WINDOW_SIZE,
                isModal: true,
                isOpen: true,
                allowClose: true,
                isMessageBox: true,
                headline: new labelViewModel({
                    ergName: 'ERROR_TITLE'
                })
            }, additionalWindowParams)),
            image: new imageViewModel({
                parentLocator: loc,
                locator: 'content.image',
                source: 'message_error.png',
                alternativeText: new labelViewModel({
                    ergName: 'ERROR_TITLE'
                })
            }),
            label: new labelViewModel({
                parentLocator: loc,
                locator: 'content.label',
                caption: caption,
                ergName: ergName
            }),
            data: new labelViewModel({
                parentLocator: loc,
                locator: 'content.data',
                isVisible: data != null,
                caption: JSON.stringify(data, null, 4)
            }),
            stack: new labelViewModel({
                parentLocator: loc,
                locator: 'content.stack',
                isVisible: stack != null && stack !== '',
                caption: stack
            }),
            buttons: [
                new buttonViewModel({
                    parentLocator: loc,
                    locator: 'button.ok',
                    isPrimaryButton: true,
                    image: new imageViewModel({
                        parentLocator: loc,
                        locator: 'button.ok.image',
                        source: GlobalConfiguration.inlineImages.WINDOW_OK,
                        alternativeText: new labelViewModel({
                            ergName: 'OKAY'
                        })
                    }),
                    label: new labelViewModel({
                        parentLocator: loc,
                        locator: 'button.ok.label',
                        ergName: 'OKAY'
                    }),
                    onClick: () => {
                        messageBoxViewModel.window.isOpen(false);

                        if (callback != null) {
                            callback();
                        }
                    }
                })
            ]
        });

        return messageBoxViewModel;
    }

    public static createErrorMessageBoxWithoutErgnames(caption: ObservableOrDefault<string>, stack?: string, data?: any, callback?: Function, additionalWindowParams?: any): MessageBoxViewModel {
        const windowViewModel = ComponentManager.getViewModel<typeof WindowViewModel>('WindowViewModel');
        const labelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');
        const imageViewModel = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
        const buttonViewModel = ComponentManager.getViewModel<typeof ButtonViewModel>('ButtonViewModel');

        const loc = 'error-message';

        const messageBoxViewModel = new MessageBoxViewModel({
            window: new windowViewModel($.extend({
                parentLocator: loc,
                locator: 'window',
                width: MessageBoxViewModel.MSG_WINDOW_SIZE,
                isModal: true,
                isOpen: true,
                allowClose: true,
                isMessageBox: true,
                headline: new
                    labelViewModel({
                        caption: 'Error'
                    })
            }, additionalWindowParams)),
            image: new imageViewModel({
                parentLocator: loc,
                locator: 'content.image',
                source: 'message_error.png',
                alternativeText: new labelViewModel({
                    ergName: 'ERROR_TITLE'
                })
            }),
            label: new labelViewModel({
                parentLocator: loc,
                locator: 'content.label',
                caption: caption
            }),
            data: new labelViewModel({
                parentLocator: loc,
                locator: 'content.data',
                isVisible: data != null,
                caption: JSON.stringify(data, null, 4)
            }),
            stack: new labelViewModel({
                parentLocator: loc,
                locator: 'content.stack',
                isVisible: stack != null && stack !== '',
                caption: stack
            }),
            buttons: [
                new buttonViewModel({
                    parentLocator: loc,
                    locator: 'button.ok',
                    isPrimaryButton: true,
                    image: new imageViewModel({
                        parentLocator: loc,
                        locator: 'button.ok.image',
                        source: GlobalConfiguration.inlineImages.WINDOW_OK,
                        alternativeText: new labelViewModel({
                            ergName: 'OKAY'
                        })
                    }),
                    label: new labelViewModel({
                        parentLocator: loc,
                        locator: 'button.ok.label',
                        caption: 'Ok'
                    }),
                    onClick: () => {
                        messageBoxViewModel.window.isOpen(false);

                        if (callback != null) {
                            callback();
                        }
                    }
                })
            ]
        });

        return messageBoxViewModel;
    }

    public static createValidationMessageBox(itemNames: any, callback?: Function): MessageBoxViewModel {
        const windowViewModel = ComponentManager.getViewModel<typeof WindowViewModel>('WindowViewModel');
        const labelViewModelT = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');
        const imageViewModel = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
        const buttonViewModel = ComponentManager.getViewModel<typeof ButtonViewModel>('ButtonViewModel');
        const loc = 'validation-message';
        let labelViewModel;

        if (itemNames) {
            labelViewModel = new labelViewModelT({
                parentLocator: loc,
                locator: 'content.label',
                ergName: 'VALIDATION_NOTICE_WITH_FIELDS',
                replacements: {
                    'fields': new labelViewModelT({
                        caption: itemNames.join(', ')
                    })
                }
            });
        } else {
            labelViewModel = new labelViewModelT({
                parentLocator: loc,
                locator: 'content.label',
                ergName: 'VALIDATION_NOTICE'
            });
        }

        const messageBoxViewModel = new MessageBoxViewModel({
            window: new windowViewModel({
                parentLocator: loc,
                locator: 'window',
                width: MessageBoxViewModel.MSG_WINDOW_SIZE,
                isModal: true,
                isOpen: true,
                allowClose: true,
                isMessageBox: true,
                headline: new labelViewModelT({
                    ergName: 'ERROR_TITLE'
                })
            }),
            image: new imageViewModel({
                parentLocator: loc,
                locator: 'content.image',
                source: 'message_error.png',
                alternativeText: new labelViewModelT({
                    ergName: 'ERROR_TITLE'
                })
            }),
            label: labelViewModel,
            buttons: [
                new buttonViewModel({
                    parentLocator: loc,
                    locator: 'button.ok',
                    isPrimaryButton: true,
                    image: new imageViewModel({
                        parentLocator: loc,
                        locator: 'button.ok.image',
                        source: GlobalConfiguration.inlineImages.WINDOW_OK,
                        alternativeText: new labelViewModelT({
                            ergName: 'OKAY'
                        })
                    }),
                    label: new labelViewModelT({
                        parentLocator: loc,
                        locator: 'button.ok.label',
                        ergName: 'OKAY'
                    }),
                    onClick: () => {
                        messageBoxViewModel.window.isOpen(false);

                        if (callback != null) {
                            callback();
                        }
                    }
                })
            ]
        });

        return messageBoxViewModel;
    }

    public static createLogoutNotificationMessageBox(callback: Function): MessageBoxViewModel {
        const windowViewModel = ComponentManager.getViewModel<typeof WindowViewModel>('WindowViewModel');
        const labelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');
        const imageViewModel = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
        const buttonViewModel = ComponentManager.getViewModel<typeof ButtonViewModel>('ButtonViewModel');

        const loc = 'logout-notification';

        const messageBoxViewModel = new MessageBoxViewModel({
            window: new windowViewModel({
                parentLocator: loc,
                locator: 'window',
                width: MessageBoxViewModel.MSG_WINDOW_SIZE,
                isModal: true,
                isOpen: false,
                allowClose: false,
                headline: new labelViewModel({
                    ergName: 'WARNING_TITLE',
                    postfix: new labelViewModel({})
                })
            }),
            image: new imageViewModel({
                parentLocator: loc,
                locator: 'content.image',
                source: GlobalConfiguration.inlineImages.NOTICE_MESSAGE_ICON,
                alternativeText: new labelViewModel({
                    ergName: 'WARNING_TITLE'
                })
            }),
            label: new labelViewModel({
                parentLocator: loc,
                locator: 'content.label',
                ergName: 'LOGOUT_WARNING'
            }),
            buttons: [
                new buttonViewModel({
                    parentLocator: loc,
                    locator: 'button.ok',
                    isPrimaryButton: true,
                    image: new imageViewModel({
                        parentLocator: loc,
                        locator: 'button.ok.image',
                        source: GlobalConfiguration.inlineImages.WINDOW_OK,
                        alternativeText: new labelViewModel({
                            ergName: 'OKAY'
                        })
                    }),
                    label: new labelViewModel({
                        parentLocator: loc,
                        locator: 'button.ok.label',
                        ergName: 'CANCEL_LOGOUT'
                    }),
                    onClick: () => {
                        if (callback != null) {
                            callback();
                        }
                    }
                })
            ]
        });

        return messageBoxViewModel;
    }

    public static createForceLogoutNotificationMessageBox(callbackLogout: Function, callbackClose: Function): MessageBoxViewModel {
        const windowViewModel = ComponentManager.getViewModel<typeof WindowViewModel>('WindowViewModel');
        const labelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');
        const imageViewModel = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
        const buttonViewModel = ComponentManager.getViewModel<typeof ButtonViewModel>('ButtonViewModel');

        const loc = 'force-logout-notification';

        const messageBoxViewModel = new MessageBoxViewModel({
            window: new windowViewModel({
                parentLocator: loc,
                locator: 'window',
                width: MessageBoxViewModel.MSG_WINDOW_SIZE,
                isModal: true,
                isOpen: false,
                allowClose: false,
                headline: new labelViewModel({
                    ergName: 'WARNING_TITLE',
                    postfix: new labelViewModel({})
                })
            }),
            image: new imageViewModel({
                parentLocator: loc,
                locator: 'content.image',
                source: GlobalConfiguration.inlineImages.NOTICE_MESSAGE_ICON,
                alternativeText: new labelViewModel({
                    ergName: 'WARNING_TITLE'
                })
            }),
            label: new labelViewModel({
                parentLocator: loc,
                locator: 'content.label',
                ergName: 'LOGOUT_FORCE_WARNING'
            }),
            buttons: [
                new buttonViewModel({
                    parentLocator: loc,
                    locator: 'button.ok',
                    isPrimaryButton: true,
                    image: new imageViewModel({
                        parentLocator: loc,
                        locator: 'button.ok.image',
                        source: GlobalConfiguration.inlineImages.WINDOW_OK,
                        alternativeText: new labelViewModel({
                            ergName: 'OKAY'
                        })
                    }),
                    label: new labelViewModel({
                        parentLocator: loc,
                        locator: 'button.ok.label',
                        ergName: 'SYSTEM_LOCK_DIALOG_LOGOUT'
                    }),
                    onClick: () => {
                        if (callbackLogout != null) {
                            callbackLogout();
                        }
                    }
                }),
                new buttonViewModel({
                    parentLocator: loc,
                    locator: 'button.cancel',
                    image: new imageViewModel({
                        parentLocator: loc,
                        locator: 'button.cancel.image',
                        source: GlobalConfiguration.inlineImages.WINDOW_CANCEL,
                        alternativeText: new labelViewModel({
                            ergName: 'CANCEL'
                        })
                    }),
                    label: new labelViewModel({
                        parentLocator: loc,
                        locator: 'button.cancel.label',
                        ergName: 'SYSTEM_LOCK_DIALOG_CLOSE'
                    }),
                    onClick: () => {
                        if (callbackClose != null) {
                            callbackClose();
                        }
                    }
                })
            ]
        });

        return messageBoxViewModel;
    }

    public static createYesNoMessageBox(caption: ObservableOrDefault<string>, ergName: ObservableOrDefault<string>, yesCallback: Function, noCallback: Function, locator: string): MessageBoxViewModel {
        const labelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');
        const imageViewModel = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
        const buttonViewModel = ComponentManager.getViewModel<typeof ButtonViewModel>('ButtonViewModel');

        const messageBoxViewModel = MessageBoxViewModel.createCustomNoticeMessageBox(caption, ergName, {
            window: {
                allowClose: false
            }
        });

        const loc = locator;

        messageBoxViewModel.buttons = [
            new buttonViewModel({
                parentLocator: loc,
                locator: 'yes',
                label: new labelViewModel({
                    parentLocator: loc,
                    locator: 'yes.label',
                    ergName: 'YES'
                }),
                image: new imageViewModel({
                    parentLocator: loc,
                    locator: 'yes.image',
                    source: GlobalConfiguration.inlineImages.WINDOW_OK,
                    alternativeText: new labelViewModel({
                        ergName: 'YES'
                    })
                }),
                onClick: () => {
                    messageBoxViewModel.window.isOpen(false);
                    yesCallback();
                }
            }),
            new buttonViewModel({
                parentLocator: loc,
                locator: 'no',
                label: new labelViewModel({
                    parentLocator: loc,
                    locator: 'no.label',
                    ergName: 'NO'
                }),
                image: new imageViewModel({
                    parentLocator: loc,
                    locator: 'no.image',
                    source: GlobalConfiguration.inlineImages.WINDOW_CANCEL,
                    alternativeText: new labelViewModel({
                        ergName: 'NO'
                    })
                }),
                onClick: () => {
                    messageBoxViewModel.window.isOpen(false);
                    noCallback();
                }
            })
        ];

        return messageBoxViewModel;
    }

    public static createOkayCancelMessageBox(caption: ObservableOrDefault<string>, ergName: ObservableOrDefault<string>, okayCallback: Function, cancelCallback: Function, locator: string): MessageBoxViewModel {
        const labelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');
        const imageViewModel = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
        const buttonViewModel = ComponentManager.getViewModel<typeof ButtonViewModel>('ButtonViewModel');

        const messageBoxViewModel = MessageBoxViewModel.createNoticeMessageBox(caption, ergName);

        const loc = locator;

        messageBoxViewModel.buttons = [
            new buttonViewModel({
                parentLocator: loc,
                isPrimaryButton: true,
                locator: 'button.ok',
                image: new imageViewModel({
                    parentLocator: loc,
                    locator: 'button.ok.image',
                    source: GlobalConfiguration.inlineImages.WINDOW_OK,
                    alternativeText: new labelViewModel({
                        ergName: 'OKAY'
                    })
                }),
                label: new labelViewModel({
                    parentLocator: loc,
                    locator: 'button.ok.label',
                    ergName: 'OKAY'
                }),
                onClick: () => {
                    messageBoxViewModel.window.isOpen(false);

                    if (okayCallback != null) {
                        okayCallback();
                    }
                }
            }),
            new buttonViewModel({
                parentLocator: loc,
                locator: 'button.cancel',
                image: new imageViewModel({
                    parentLocator: loc,
                    locator: 'button.cancel.image',
                    source: GlobalConfiguration.inlineImages.WINDOW_CANCEL,
                    alternativeText: new labelViewModel({
                        ergName: 'CANCEL'
                    })
                }),
                label: new labelViewModel({
                    parentLocator: loc,
                    locator: 'button.cancel.label',
                    ergName: 'CANCEL'
                }),
                onClick: () => {
                    messageBoxViewModel.window.isOpen(false);

                    if (cancelCallback != null) {
                        cancelCallback();
                    }
                }
            })
        ];

        return messageBoxViewModel;
    }
}

type customParamsType = {
    window?: {
        headline?: { ergName?: string },
        width?: string | number,
        allowClose?: boolean,
    },
    image?: {
        source?: string
    },
    button?: {
        image?: { source?: string },
        label?: { ergName?: string }
    }
    buttons?: Array<ButtonViewModel>;
};

ComponentManager.setViewModel('MessageBoxViewModel', MessageBoxViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');